import { useState, useMemo, useCallback } from 'react';
import s from './schedule-modal.module.scss';
import { motion, AnimatePresence } from 'framer-motion';
import { Modal } from '@shared/ui/modal/modal';
import { useScheduleModal } from '../../model/use-schedule-modal';
import { useAllAppointments } from '@entities/schedule/api/useAllAppointments';
import { useTimeSlots } from '@features/schedule/api/useTimeSlots';
import { useProfile } from '@shared/api/useProfile';
import { queryClient } from '@shared/api/query-client';
import { useNavigate } from 'react-router-dom';
import {
  addDays,
  addHours,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { ru, enUS } from 'date-fns/locale';
import { IconCalendarShare, IconEdit } from '@tabler/icons-react';
import { TZDate } from '@date-fns/tz';
import { toTzDate } from '../../lib/date';
import { ScheduleTabs } from './schedule-tabs';
import { CalendarHeader } from './calendar-header';
import { MonthCalendar } from './month-calendar';
import { WeekCalendar } from './week-calendar';
import { AppointmentsList } from './appointments-list';
import { CtaCard } from './cta-card';
import { TeacherCta } from './teacher-cta';
import { TeacherEditMode } from './teacher-edit-mode';
import { useEditScheduleModal } from '@features/teacher/edit-schedule/model/use-edit-schedule-modal';
import { useCalendarSyncModal } from '@features/calendar-sync';

export const ScheduleModal = () => {
  const { isOpen, close, modalParams } = useScheduleModal();
	const { open: openEditSchedule } = useEditScheduleModal()
	const { open: openCalendarSync } = useCalendarSyncModal()
  const { isTeacher } = modalParams || {};
  const { data: profile } = useProfile();
  const timezone = profile?.timezone;
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState('week'); // 'month' | 'week'
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(new Date()));
  const [isEditMode, setIsEditMode] = useState(false);

  const displayedRange = useMemo(() => {
    if (viewMode === 'month') {
      const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 });
      const end = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 });
      return { from: start.toISOString(), to: end.toISOString() };
    }
    const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
    const end = endOfWeek(selectedDate, { weekStartsOn: 1 });
    return { from: start.toISOString(), to: end.toISOString() };
  }, [viewMode, currentMonth, selectedDate]);

  const { data: appointments, isFetching } = useAllAppointments(
    { from: displayedRange.from, to: displayedRange.to },
    { enabled: isOpen }
  );
  const { data: timeSlots } = useTimeSlots(isTeacher);

  const selectedWeekdayKey = useMemo(
    () => format(selectedDate, 'EEEE', { locale: enUS }).toLowerCase(),
    [selectedDate]
  );

  const appointmentsByDay = useMemo(() => {
    if (!appointments) return {};
    const map = {};
    appointments.forEach((apt) => {
      const key = format(toTzDate(new Date(apt.starts_at), timezone), 'yyyy-MM-dd', {
        locale: ru,
      });
      if (!map[key]) map[key] = [];
      map[key].push(apt);
    });
    return map;
  }, [appointments, timezone]);

  const selectedDayAppointments = useMemo(() => {
    const key = format(toTzDate(selectedDate, timezone), 'yyyy-MM-dd', { locale: ru });
    return appointmentsByDay[key] || [];
  }, [appointmentsByDay, selectedDate, timezone]);

  const hasAvailableFutureSlots = useMemo(() => {
    if (!isTeacher || !timeSlots || !timezone) return false;

    const now = toTzDate(new Date(), timezone);
    const minTime = addHours(now, 1);
    const dateStr = format(toTzDate(selectedDate, timezone), 'yyyy-MM-dd');

    const bookedTimes = new Set(
      (appointmentsByDay[dateStr] || [])
        .map((apt) => format(toTzDate(new Date(apt.starts_at), timezone), 'HH:mm'))
    );

    return timeSlots.some((slot) => {
      if (slot.weekday !== selectedWeekdayKey) return false;
      if (bookedTimes.has(slot.time)) return false;
      const [hours, minutes] = slot.time.split(':');
      const slotDateTime = new TZDate(`${dateStr}T${hours}:${minutes}:00`, timezone);
      return slotDateTime.getTime() >= minTime.getTime();
    });
  }, [isTeacher, timeSlots, timezone, selectedWeekdayKey, selectedDate, appointmentsByDay]);

  const monthCalendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);

  const weekCalendarDays = useMemo(() => {
    const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end: addDays(start, 6) });
  }, [selectedDate]);

  const handlePrevMonth = useCallback(() => {
    setCurrentMonth((prev) => {
      const next = subMonths(prev, 1);
      setSelectedDate(startOfMonth(next));
      return next;
    });
  }, []);

  const handleNextMonth = useCallback(() => {
    setCurrentMonth((prev) => {
      const next = addMonths(prev, 1);
      setSelectedDate(startOfMonth(next));
      return next;
    });
  }, []);

  const handlePrevWeek = useCallback(() => {
    setSelectedDate((prev) =>
      startOfWeek(addWeeks(prev, -1), { weekStartsOn: 1 })
    );
  }, []);

  const handleNextWeek = useCallback(() => {
    setSelectedDate((prev) =>
      startOfWeek(addWeeks(prev, 1), { weekStartsOn: 1 })
    );
  }, []);

  const handleSelectMonthDay = useCallback((date) => {
    setSelectedDate(date);
    setCurrentMonth((prev) =>
      isSameMonth(date, prev) ? prev : startOfMonth(date)
    );
  }, []);

  const handleSelectWeekDay = useCallback((date) => {
    setSelectedDate(date);
  }, []);

  const handleSwitchToMonth = useCallback(() => {
    const nextMonth = startOfMonth(selectedDate);
    setViewMode('month');
    setCurrentMonth((prev) =>
      isSameMonth(nextMonth, prev) ? prev : nextMonth
    );
    setSelectedDate(nextMonth);
  }, [selectedDate]);

  const handleSwitchToWeek = useCallback(() => {
    setViewMode('week');
    setSelectedDate((prev) => startOfWeek(prev, { weekStartsOn: 1 }));
  }, []);

  const handleBookLesson = useCallback(() => {
    close();
    navigate('/teachers');
  }, [close, navigate]);

  const handleEnterEditMode = useCallback(() => {
    setIsEditMode(true);
  }, []);

  const handleSaveEditMode = useCallback(() => {
    queryClient.invalidateQueries(['appointments', displayedRange]);
    setIsEditMode(false);
  }, [displayedRange]);

  const handleCancelEditMode = useCallback(() => {
    setIsEditMode(false);
  }, []);

  const handleClose = useCallback(() => {
    setIsEditMode(false);
    close();
  }, [close]);

  const headerTitleChildren = (
    <div className={s.titleButtons}>
      <button
        className={s.titleButton}
        onClick={openCalendarSync}
        type="button"
        aria-label="Синхронизировать календарь"
      >
        <IconCalendarShare size={18} />
      </button>
      {isTeacher && (
        <button
          className={s.titleButton}
          onClick={openEditSchedule}
          type="button"
          aria-label="Редактировать расписание"
        >
          <IconEdit size={18} />
        </button>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      close={handleClose}
      title="Расписание"
      titleChildren={headerTitleChildren}
      style={{
        content: {
          width: 'calc(100% - 40px)',
          maxWidth: '420px',
          maxHeight: 'calc(100svh - 40px)',
          padding: '20px',
          overflow: 'auto',
        },
      }}
    >
      <div className={s.wrapper}>
        <ScheduleTabs
          viewMode={viewMode}
          onSwitchToMonth={handleSwitchToMonth}
          onSwitchToWeek={handleSwitchToWeek}
        />

        <div className={s.calendar}>
          <CalendarHeader
            viewMode={viewMode}
            currentMonth={currentMonth}
            weekCalendarDays={weekCalendarDays}
            onPrev={viewMode === 'month' ? handlePrevMonth : handlePrevWeek}
            onNext={viewMode === 'month' ? handleNextMonth : handleNextWeek}
          />

          <AnimatePresence mode="wait" initial={false}>
            {viewMode === 'month' ? (
              <motion.div
                key="month"
                className={s.calendarView}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 16 }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
              >
                <MonthCalendar
                  days={monthCalendarDays}
                  selectedDate={selectedDate}
                  currentMonth={currentMonth}
                  appointmentsByDay={appointmentsByDay}
                  timezone={timezone}
                  onSelectDay={handleSelectMonthDay}
                />
              </motion.div>
            ) : (
              <motion.div
                key="week"
                className={s.calendarView}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
              >
                <WeekCalendar
                  days={weekCalendarDays}
                  selectedDate={selectedDate}
                  appointmentsByDay={appointmentsByDay}
                  timezone={timezone}
                  onSelectDay={handleSelectWeekDay}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={`appointments-${format(
              toTzDate(selectedDate, timezone),
              'yyyy-MM-dd',
              { locale: ru }
            )}-${viewMode}-${isEditMode}`}
            className={s.appointmentsSection}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
          >
            {isTeacher && isEditMode ? (
              <TeacherEditMode
                selectedDate={selectedDate}
                appointments={selectedDayAppointments}
                timeSlots={timeSlots}
                selectedWeekdayKey={selectedWeekdayKey}
                rangeParams={displayedRange}
                onSave={handleSaveEditMode}
                onCancel={handleCancelEditMode}
              />
            ) : (
              <AppointmentsList
                isFetching={isFetching}
                appointments={selectedDayAppointments}
                isTeacher={isTeacher}
              />
            )}
          </motion.div>
        </AnimatePresence>

        {isTeacher ? (
          !isEditMode && hasAvailableFutureSlots && <TeacherCta onAdd={handleEnterEditMode} />
        ) : (
          <CtaCard onBook={handleBookLesson} />
        )}
      </div>
    </Modal>
  );
};
